import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Control, useForm } from 'react-hook-form';
import { LayoutStoreInstance } from '@/LayoutStore';
import {
  MerchantOrderItemUnit,
  MerchantOrderStatusEnum,
} from '@/graphql/generatedTypes';
import { useGetMerchantOrderSharedLinkLazyQuery } from '@/graphql/queries/getMerchantOrderSharedLink.generated';
import { useUpdateMerchantOrderSharedLinkMutation } from '@/graphql/mutations/updateMerchantOrderSharedLink.generated';
import i18n from '@/utils/i18n';
import type { ItemTypeEntry } from '../PublicEventOrderForm/OrderFormContext';
import {
  buildItemTypeEntries,
  cloneItemTypeEntries,
} from '../PublicEventOrderForm/utils';

type LoadingStatus =
  | 'initializing'
  | 'loading'
  | 'ready'
  | 'submitting'
  | 'error'
  | 'not-found';

export type ContainerEntry = {
  info: {
    id: number;
    name: string;
    imageUrl: string | null | undefined;
    segment: string;
    packingUnit: number;
    sizeHelper: string;
    color: string | null | undefined;
  };
  quantity: number;
  unit: MerchantOrderItemUnit;
  price: {
    cleaningPrice: number;
    depositPrice: number;
    lostPrice: number;
    rentalPrice: number;
  };
};

type ItemLocationEntry = {
  itemLocationName: string;
  itemLocationId: string;
  containers: {
    [containerTypeId: PropertyKey]: ContainerEntry;
  };
  itemTypes: {
    [itemTypeId: PropertyKey]: ItemTypeEntry;
  };
};

export type ContainerOrder = {
  [itemLocationId: PropertyKey]: ItemLocationEntry;
};

type FormValues = {
  comment: string;
  status: MerchantOrderStatusEnum | null;
};

type EventManager = {
  name: string | null;
  phone: string | null;
  mobile: string | null;
};

type EditEventMerchantOrderContextValue = {
  formControl: Control<FormValues>;
  loadingStatus: LoadingStatus;
  order: ContainerOrder;
  updateOrder: (params: {
    itemLocationId: string;
    containerTypeId: number;
    quantity: number;
  }) => void;
  updateItemTypeOrder: (params: {
    itemLocationId: string;
    itemTypeId: number;
    quantity: number;
  }) => void;
  submitForm: () => void;
  sendSalesOrder: () => Promise<boolean>;
  resetForm: () => void;
  sharedLinkId: string;
  merchantOrderId: string | null;
  eventId: string | null;
  eventAccountId: string | null;
  eventName: string | null;
  eventAccountName: string | null;
  eventAccountEmail: string | null;
  hasOrderChanged: boolean;
  hasOpsOrder: boolean;
  orderStatus: MerchantOrderStatusEnum | null;
  setOrderStatus: (status: MerchantOrderStatusEnum) => void;
  salesOrderUrl: string | null;
  salesOrderZohoId: string | null;
  salesOrderSendReferenceId: string | null;
  // Additional fields for OrderInfo and CostSummary
  eventStartDate: string | null;
  eventEndDate: string | null;
  formDeadline: string | null;
  eventManager: EventManager | null;
  currency: string;
  taxPercentage: number;
  // Minimum cleaning fee rate in % of rented pcs; null = no minimum,
  // only items returned dirty are billed for cleaning
  minimumCleaningFeeRate: number | null;
  canSendSalesOrder: boolean;
  onStatusChangedToApproved: (callback: () => void) => void;
};

const EditEventMerchantOrderContext = createContext<
  EditEventMerchantOrderContextValue | undefined
>(undefined);

export function EditEventMerchantOrderProvider({
  sharedLinkId,
  children,
}: {
  sharedLinkId: string;
  children: React.ReactNode;
}) {
  const [loadingStatus, setLoadingStatus] =
    useState<LoadingStatus>('initializing');
  const [order, setOrder] = useState<ContainerOrder>({});
  const [initialOrder, setInitialOrder] = useState<ContainerOrder>({});
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventAccountId, setEventAccountId] = useState<string | null>(null);
  const [eventName, setEventName] = useState<string | null>(null);
  const [eventAccountName, setEventAccountName] = useState<string | null>(null);
  const [eventAccountEmail, setEventAccountEmail] = useState<string | null>(
    null,
  );
  const [merchantOrderId, setMerchantOrderId] = useState<string | null>(null);
  const [hasOpsOrder, setHasOpsOrder] = useState(false);
  const [orderStatus, setOrderStatus] =
    useState<MerchantOrderStatusEnum | null>(null);
  const [initialStatus, setInitialStatus] =
    useState<MerchantOrderStatusEnum | null>(null);
  const [salesOrderUrl, setSalesOrderUrl] = useState<string | null>(null);
  const [salesOrderZohoId, setSalesOrderZohoId] = useState<string | null>(null);
  const [salesOrderSendReferenceId, setSalesOrderSendReferenceId] = useState<
    string | null
  >(null);
  const [eventStartDate, setEventStartDate] = useState<string | null>(null);
  const [eventEndDate, setEventEndDate] = useState<string | null>(null);
  const [formDeadline, setFormDeadline] = useState<string | null>(null);
  const [eventManager, setEventManager] = useState<EventManager | null>(null);
  const [taxPercentage, setTaxPercentage] = useState<number>(19); // Default VAT rate
  const [currency, setCurrency] = useState<string>('EUR'); // Default currency
  const [minimumCleaningFeeRate, setMinimumCleaningFeeRate] = useState<
    number | null
  >(null);

  // Callback ref for when status changes to approved
  const onApprovedCallbackRef = React.useRef<(() => void) | null>(null);

  const [fetchSharedLink] = useGetMerchantOrderSharedLinkLazyQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [updateSharedLink] = useUpdateMerchantOrderSharedLinkMutation({
    errorPolicy: 'all',
  });

  const hasOrderChanged = useMemo(() => {
    const orderChanged = JSON.stringify(order) !== JSON.stringify(initialOrder);
    const statusChanged = orderStatus !== initialStatus;
    return orderChanged || statusChanged;
  }, [order, initialOrder, orderStatus, initialStatus]);

  const disabled = ['submitting', 'initializing', 'loading'].includes(
    loadingStatus,
  );

  const form = useForm<FormValues>({
    defaultValues: { comment: '', status: null },
    disabled,
  });

  // Extract data loading logic into a reusable function
  const loadSharedLinkData = useCallback(() => {
    if (!sharedLinkId) {
      return;
    }

    setLoadingStatus('loading');

    fetchSharedLink({
      variables: { id: sharedLinkId },
    })
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to load shared link:', error);
          LayoutStoreInstance.setError(
            i18n.t('Common:error') + ': ' + error.message,
          );
          setLoadingStatus('error');
          return;
        }

        const sharedLink = data?.getMerchantOrderSharedLink;
        if (!sharedLink?.event_account) {
          setLoadingStatus('not-found');
          return;
        }

        setEventId(sharedLink.event?.id || null);
        setEventAccountId(sharedLink.event_account?.id || null);
        setEventName(sharedLink.event?.name || null);
        setEventAccountName(sharedLink.event_account?.name || null);
        setEventAccountEmail(sharedLink.event_account?.email || null);
        setMerchantOrderId(sharedLink.merchant_order?.id || null);
        setHasOpsOrder(!!sharedLink.merchant_order);
        setSalesOrderUrl(sharedLink.sales_order_url || null);
        setSalesOrderZohoId(sharedLink.sales_order_external_zoho_id || null);
        setSalesOrderSendReferenceId(
          sharedLink.sales_order_send_reference_id || null,
        );
        setEventStartDate(sharedLink.event?.start_date || null);
        setEventEndDate(sharedLink.event?.end_date || null);
        setFormDeadline(sharedLink.event?.merchant_order_form_deadline || null);

        // Set currency and VAT rate from backend
        if (sharedLink.event?.currency) {
          setCurrency(sharedLink.event.currency);
        }
        if (sharedLink.vat_rate != null) {
          setTaxPercentage(sharedLink.vat_rate);
        }
        setMinimumCleaningFeeRate(
          sharedLink.event?.minimum_cleaning_fee_rate ?? null,
        );

        // Set event manager info
        const manager = sharedLink.event?.event_manager;
        if (manager) {
          setEventManager({
            name: manager.full_name || null,
            phone: manager.phone || null,
            mobile: manager.mobile || null,
          });
        }

        // Set the comment and status from the existing order
        if (sharedLink.merchant_order?.comment) {
          form.setValue('comment', sharedLink.merchant_order.comment);
        } else {
          form.setValue('comment', '');
        }
        if (sharedLink.merchant_order?.status) {
          const status = sharedLink.merchant_order
            .status as MerchantOrderStatusEnum;
          setOrderStatus(status);
          setInitialStatus(status);
          form.setValue('status', status);
        } else {
          setOrderStatus(null);
          setInitialStatus(null);
          form.setValue('status', null);
        }

        // Transform shared link data to ContainerOrder format
        const transformedOrder: ContainerOrder = {};

        if (
          sharedLink.event?.allowed_container_types &&
          sharedLink.item_locations
        ) {
          const containerTypes = sharedLink.event.allowed_container_types;
          const eventPrices = sharedLink.event.prices || [];
          const itemLocations = sharedLink.item_locations;
          const orderItems =
            sharedLink.merchant_order?.latest_revision?.containers || [];
          const itemTypeLines =
            sharedLink.merchant_order?.latest_revision?.items || [];
          const defaultItemTypes = buildItemTypeEntries(
            sharedLink.event.allowed_item_types,
          );

          itemLocations.forEach(
            (itemLocation: {
              id: string;
              type?: string | null;
              name?: string | null;
            }) => {
              const locationId = itemLocation.id;
              const containersForLocation: ItemLocationEntry['containers'] = {};

              containerTypes.forEach((containerType) => {
                // Find the price info for this container type
                const priceInfo = eventPrices.find(
                  (p) => p.container_type_id === containerType.id,
                );

                // Find the order item for this container type and location
                const orderItem = orderItems.find(
                  (item) =>
                    item.type_id === containerType.id &&
                    item.item_location_id === locationId,
                );

                containersForLocation[containerType.id] = {
                  info: {
                    id: containerType.id,
                    name: containerType.name,
                    imageUrl: containerType.image_url || containerType.image,
                    segment: containerType.segment || '',
                    packingUnit: containerType.packing_unit || 1,
                    sizeHelper: containerType.size_helper || '',
                    color: containerType.color_hex_code,
                  },
                  quantity: orderItem?.amount_int || 0,
                  unit: orderItem?.unit || MerchantOrderItemUnit.Boxes,
                  price: {
                    cleaningPrice: priceInfo?.cleaning_price || 0,
                    depositPrice: priceInfo?.deposit_price || 0,
                    lostPrice: priceInfo?.lost_price || 0,
                    rentalPrice: priceInfo?.rental_price || 0,
                  },
                };
              });

              const itemTypesForLocation =
                cloneItemTypeEntries(defaultItemTypes);
              itemTypeLines.forEach((line) => {
                const entry =
                  line.item_location_id === locationId
                    ? itemTypesForLocation[line.item_type_id]
                    : undefined;
                if (entry) {
                  entry.quantity = line.amount_int;
                }
              });

              transformedOrder[locationId] = {
                itemLocationId: locationId,
                itemLocationName: itemLocation.name || itemLocation.type || '',
                containers: containersForLocation,
                itemTypes: itemTypesForLocation,
              };
            },
          );
        }

        setOrder(transformedOrder);
        setInitialOrder(JSON.parse(JSON.stringify(transformedOrder)));
        setLoadingStatus('ready');
      })
      .catch((error) => {
        console.error('Failed to load shared link:', error);
        LayoutStoreInstance.setError(i18n.t('Common:error') + ': ' + error);
        setLoadingStatus('error');
      });
  }, [sharedLinkId, fetchSharedLink, form]);

  // Load shared link data on mount
  useEffect(() => {
    loadSharedLinkData();
  }, [loadSharedLinkData]);

  // Reset form function - reloads data from backend
  const resetForm = useCallback(() => {
    loadSharedLinkData();
  }, [loadSharedLinkData]);

  const updateOrder = useCallback(
    ({
      itemLocationId,
      containerTypeId,
      quantity,
    }: {
      itemLocationId: string;
      containerTypeId: number;
      quantity: number;
    }) => {
      setOrder((prev) => {
        if (!prev[itemLocationId]?.containers[containerTypeId]) {
          return prev;
        }
        const newOrder = {
          ...prev,
          [itemLocationId]: {
            ...prev[itemLocationId],
            containers: {
              ...prev[itemLocationId].containers,
              [containerTypeId]: {
                ...prev[itemLocationId].containers[containerTypeId],
                quantity,
              },
            },
          },
        };
        return newOrder;
      });
    },
    [],
  );

  const updateItemTypeOrder = useCallback(
    ({
      itemLocationId,
      itemTypeId,
      quantity,
    }: {
      itemLocationId: string;
      itemTypeId: number;
      quantity: number;
    }) => {
      setOrder((prev) => {
        const entry = prev[itemLocationId]?.itemTypes[itemTypeId];
        if (!entry) {
          return prev;
        }
        return {
          ...prev,
          [itemLocationId]: {
            ...prev[itemLocationId],
            itemTypes: {
              ...prev[itemLocationId].itemTypes,
              [itemTypeId]: { ...entry, quantity },
            },
          },
        };
      });
    },
    [],
  );

  // Check if the current status allows sending a sales order
  // Use initialStatus (backend status) instead of orderStatus (local state)
  const canSendSalesOrder = useMemo(() => {
    return initialStatus === MerchantOrderStatusEnum.Approved;
  }, [initialStatus]);

  // Send sales order - changes status to APPROVED_SALES_ORDER_SENT without updating items or comment
  const sendSalesOrder = useCallback(async (): Promise<boolean> => {
    if (!canSendSalesOrder) {
      return false;
    }

    setLoadingStatus('submitting');

    try {
      const { data, errors } = await updateSharedLink({
        variables: {
          input: {
            merchant_order_shared_link_id: sharedLinkId,
            status: MerchantOrderStatusEnum.SalesOrderSent,
            // Do not include merchant_order_items or comment
          },
        },
      });

      if (errors && errors.length > 0) {
        console.error('sendSalesOrder errors', errors);
        // Extract reason from error extensions if available
        const errorMessages = errors.map((e) => {
          const reason =
            e.extensions?.exception?.reason || e.extensions?.reason;
          return reason || e.message;
        });
        LayoutStoreInstance.setError(
          i18n.t('Common:error') + ': ' + errorMessages.join(', '),
        );
        setLoadingStatus('ready');
        return false;
      }

      if (data?.updateMerchantOrderSharedLink) {
        LayoutStoreInstance.setSuccess(
          i18n.t('MerchantOrder:salesOrderSent') ||
            'Sales order sent successfully',
        );

        // Re-fetch the shared link to get all updated data
        const { data: refreshedData } = await fetchSharedLink({
          variables: { id: sharedLinkId },
        });

        const sharedLink = refreshedData?.getMerchantOrderSharedLink;
        if (sharedLink) {
          setSalesOrderUrl(sharedLink.sales_order_url || null);
          setSalesOrderZohoId(sharedLink.sales_order_external_zoho_id || null);
          setSalesOrderSendReferenceId(
            sharedLink.sales_order_send_reference_id || null,
          );

          // Update status from refreshed data
          if (sharedLink.merchant_order?.status) {
            const newStatus = sharedLink.merchant_order
              .status as MerchantOrderStatusEnum;
            setOrderStatus(newStatus);
            setInitialStatus(newStatus);
          }
        }

        setLoadingStatus('ready');
        return true;
      } else {
        LayoutStoreInstance.setError(i18n.t('Common:error'));
        setLoadingStatus('ready');
        return false;
      }
    } catch (error) {
      console.error('sendSalesOrder error', error);
      LayoutStoreInstance.setError(i18n.t('Common:error') + ': ' + error);
      setLoadingStatus('ready');
      return false;
    }
  }, [canSendSalesOrder, sharedLinkId, updateSharedLink, fetchSharedLink]);

  const submitForm = form.handleSubmit(async (formData) => {
    setLoadingStatus('submitting');

    // Track if status is being changed to Approved
    const isChangingToApproved =
      orderStatus === MerchantOrderStatusEnum.Approved &&
      initialStatus !== MerchantOrderStatusEnum.Approved;

    const orderItems: Array<{
      item_location_id: string;
      type_id: number;
      item_type_id?: number;
      amount_int: number;
      unit: MerchantOrderItemUnit;
    }> = [];

    Object.values(order).forEach((itemLocation) => {
      Object.values(itemLocation.containers).forEach((container) => {
        if (container.quantity === 0) {
          return;
        }
        orderItems.push({
          item_location_id: itemLocation.itemLocationId,
          type_id: container.info.id,
          amount_int: container.quantity,
          unit: container.unit,
        });
      });
      Object.values(itemLocation.itemTypes).forEach((line) => {
        if (line.quantity === 0) {
          return;
        }
        orderItems.push({
          item_location_id: itemLocation.itemLocationId,
          type_id: 0,
          item_type_id: line.info.id,
          amount_int: line.quantity,
          unit: MerchantOrderItemUnit.Pieces,
        });
      });
    });

    try {
      const { data, errors } = await updateSharedLink({
        variables: {
          input: {
            merchant_order_shared_link_id: sharedLinkId,
            comment: formData.comment,
            merchant_order_items: orderItems,
            status: orderStatus || undefined,
          },
        },
      });

      if (errors && errors.length > 0) {
        console.error('submitForm errors', errors);
        // Extract reason from error extensions if available
        const errorMessages = errors.map((e) => {
          const reason =
            e.extensions?.exception?.reason || e.extensions?.reason;
          return reason || e.message;
        });
        LayoutStoreInstance.setError(
          i18n.t('Common:error') + ': ' + errorMessages.join(', '),
        );
        setLoadingStatus('ready');
        return;
      }

      if (data?.updateMerchantOrderSharedLink) {
        LayoutStoreInstance.setSuccess(
          i18n.t('Common:success') || 'Order updated successfully',
        );
        // Update the initial order to reflect the saved state
        setInitialOrder(JSON.parse(JSON.stringify(order)));
        setInitialStatus(orderStatus);
        // The mutation returns EventMerchantOrderSharedLinkInfo which uses ops_order
        if (data.updateMerchantOrderSharedLink.ops_order?.id) {
          setMerchantOrderId(data.updateMerchantOrderSharedLink.ops_order.id);
          setHasOpsOrder(true);
        }
        // Update status if returned from the mutation
        if (data.updateMerchantOrderSharedLink.ops_order?.status) {
          const newStatus = data.updateMerchantOrderSharedLink.ops_order
            .status as MerchantOrderStatusEnum;
          setOrderStatus(newStatus);
          setInitialStatus(newStatus);
        }
        // Re-fetch the shared link to get updated sales order info
        await fetchSharedLink({ variables: { id: sharedLinkId } }).then(
          ({ data: refreshedData }) => {
            const sharedLink = refreshedData?.getMerchantOrderSharedLink;
            if (sharedLink) {
              setSalesOrderUrl(sharedLink.sales_order_url || null);
              setSalesOrderZohoId(
                sharedLink.sales_order_external_zoho_id || null,
              );
              setSalesOrderSendReferenceId(
                sharedLink.sales_order_send_reference_id || null,
              );
            }
          },
        );

        // Call the callback if status was changed to approved
        if (isChangingToApproved && onApprovedCallbackRef.current) {
          onApprovedCallbackRef.current();
        }

        setLoadingStatus('ready');
      } else {
        LayoutStoreInstance.setError(i18n.t('Common:error'));
        setLoadingStatus('ready');
      }
    } catch (error) {
      console.error('submitForm error', error);
      LayoutStoreInstance.setError(i18n.t('Common:error') + ': ' + error);
      setLoadingStatus('ready');
    }
  });

  const onStatusChangedToApproved = useCallback((callback: () => void) => {
    onApprovedCallbackRef.current = callback;
  }, []);

  const value: EditEventMerchantOrderContextValue = {
    formControl: form.control,
    loadingStatus,
    order,
    updateOrder,
    updateItemTypeOrder,
    submitForm,
    sendSalesOrder,
    resetForm,
    sharedLinkId,
    merchantOrderId,
    eventId,
    eventAccountId,
    eventName,
    eventAccountName,
    eventAccountEmail,
    hasOrderChanged,
    hasOpsOrder,
    orderStatus,
    setOrderStatus,
    salesOrderUrl,
    salesOrderZohoId,
    salesOrderSendReferenceId,
    eventStartDate,
    eventEndDate,
    formDeadline,
    eventManager,
    currency,
    taxPercentage,
    minimumCleaningFeeRate,
    canSendSalesOrder,
    onStatusChangedToApproved,
  };

  return (
    <EditEventMerchantOrderContext.Provider value={value}>
      {children}
    </EditEventMerchantOrderContext.Provider>
  );
}

export const useEditEventMerchantOrderContext =
  (): EditEventMerchantOrderContextValue => {
    const ctx = useContext(EditEventMerchantOrderContext);
    if (!ctx) {
      throw new Error(
        'useEditEventMerchantOrderContext must be used within EditEventMerchantOrderProvider',
      );
    }
    return ctx;
  };
